import {getCustomers, getSuppliers, useSubAccounts} from '@/subaccount/subAccount.service'
import {computed, ref} from 'vue'
import {DateRange} from '@/dateUtils/DateRange'
import {getPersistedRangeOr} from '@/dateUtils/dateRange.service'
import {endOfYear, format, startOfYear} from 'date-fns'
import _ from 'lodash'
import {myOrgs, selectedOrg} from '@/tenants/org.service'
import {Tenant} from '@/tenants/Tenant'
import {BookkeepingAccount} from '@/bookkeeping-account/BookkeepingAccount'
import {CUSTOMER, SubAccount, SUPPLIER} from '@/subaccount/types/SubAccount'
import {LedgerSubAccount} from '@/subaccount/types/SubAccountLedger'
import {getBookeepingAccounts} from '@/bookkeeping-account/bookkeepingAccount.service'
import {CUSTOMER_ACCOUNT_NO, SUPPLIER_ACCOUNT_NO} from '@/bookkeeping-account/accountTypes'
import {getGeneralLedger} from '@/genleg/ledger.service'
import {GenLegQuery} from '@/genleg/GenLegQuery'
import emitter from '@/services/infrastructure/emitter'
import {ORG_SELECTED} from '@/types/events'
import {LedgerAccount} from '@/types/reports/LedgerAccount'
import {get, post} from '@/services/infrastructure/api.service'
import {CrossTenantAccountLink} from '@/cross-tenant/CrossTenantAccountLink'


const crossTenantAccounts = ref<CrossTenantAccountLink[]>([])

export default () => {
    const {customers: localCustomers, suppliers: localSuppliers, fetchSubAccountLedger} = useSubAccounts()

    const _dateRange = ref<DateRange>(getPersistedRangeOr({
        start: startOfYear(new Date()),
        end: endOfYear(new Date())
    }))

    const dateRange = computed<DateRange>({
        get: () => _dateRange.value,
        set: (value: DateRange) => {
            _dateRange.value = value
            fetchLocalLedger(localAccount.value)
            fetchLocalSubLedger(localSubAccount.value)
            fetchRemoteLedger(remoteAccount.value)
            fetchRemoteSubLedger(remoteSubAccount.value)
        }
    })

    const ledgerDateRange = computed(() => {
        return {
            fromDate: format(dateRange.value.start, 'yyyy-MM-dd'),
            toDate: format(dateRange.value.end, 'yyyy-MM-dd')
        }
    })

    const localSearchParams = computed<GenLegQuery>(() => ({
        fromDate: format(dateRange.value.start, 'yyyy-MM-dd'),
        toDate: format(dateRange.value.end, 'yyyy-MM-dd'),
    }))

    const remoteSearchParams = computed<GenLegQuery>(() => ({
        fromDate: format(dateRange.value.start, 'yyyy-MM-dd'),
        toDate: format(dateRange.value.end, 'yyyy-MM-dd'),
    }))

    const remoteOrgs = computed(() => {
        return _.sortBy(myOrgs.value, 'organizationName').filter(org => org.organizationId !== selectedOrg.value?.organizationId)
    })

    const getRemoteTenantName = (tenantId: string) => {
        const tenant = remoteOrgs.value.find(org => org.organizationId === tenantId)
        return tenant ? tenant.organizationName : tenantId
    }

    const remoteTenant = ref<Tenant>()
    const remoteAccounts = ref<BookkeepingAccount[]>([])
    const remoteCustomers = ref<SubAccount[]>([])
    const remoteSuppliers = ref<SubAccount[]>([])
    const localAccount = ref<string>()
    const localSubAccount = ref<string>()
    const remoteAccount = ref<string>()
    const remoteSubAccount = ref<string>()

    const localLedger = ref<LedgerAccount | LedgerSubAccount>()
    const remoteLedger = ref<LedgerAccount | LedgerSubAccount>()

    const isAccountLedger = (ledger: LedgerAccount | LedgerSubAccount | undefined) => {
        const accountLedger = ledger as LedgerAccount
        return accountLedger && !!accountLedger.accountNo
    }

    const isSubAccountLedger = (ledger: LedgerAccount | LedgerSubAccount | undefined) => {
        return ledger && (ledger as LedgerSubAccount).subAccountUuid !== undefined
    }

    const getLedgerOutgoingBalance = (ledger: LedgerAccount | LedgerSubAccount | undefined) => {
        if (isAccountLedger(ledger)) {
            return (ledger as LedgerAccount).outgoingBalance
        } else if (isSubAccountLedger(ledger)) {
            return (ledger as LedgerSubAccount).outgoing
        }
        return 0
    }

    const localBalance = computed(() => {
        return getLedgerOutgoingBalance(localLedger.value)
    })

    const remoteBalance = computed(() => {
        return getLedgerOutgoingBalance(remoteLedger.value)
    })

    const computedRemote = computed({
        get: () => remoteTenant.value,
        set: (value: Tenant) => {
            remoteTenant.value = value
            fetchRemoteAccounts()
            fetchRemoteCustomers()
            fetchRemoteSuppliers()
        }
    })

    const fetchRemoteAccounts = async () => {
        if (remoteTenant.value) {
            remoteAccounts.value = await getBookeepingAccounts(remoteTenant.value.organizationId)
        } else {
            remoteAccounts.value = []
        }
    }

    const fetchRemoteCustomers = async () => {
        if (remoteTenant.value) {
            remoteCustomers.value = await getCustomers(remoteTenant.value.organizationId)
        } else {
            remoteCustomers.value = []
        }
    }

    const fetchRemoteSuppliers = async () => {
        if (remoteTenant.value) {
            remoteSuppliers.value = await getSuppliers(remoteTenant.value.organizationId)
        } else {
            remoteSuppliers.value = []
        }
    }

    const fetchLocalLedger = async (accountNo?: string) => {
        if (accountNo && ![CUSTOMER_ACCOUNT_NO, SUPPLIER_ACCOUNT_NO].includes(accountNo)) {
            const genLeg = await getGeneralLedger({
                    accountNo,
                    ...ledgerDateRange.value
                }
            )
            if (genLeg && genLeg.ledgerAccounts && genLeg.ledgerAccounts.length === 1) {
                localLedger.value = genLeg.ledgerAccounts[0]
            } else {
                localLedger.value = undefined
            }
        } else if(!accountNo) {
            localLedger.value = undefined
        }

    }

    const fetchRemoteLedger = async (accountNo?: string) => {
        if (remoteTenant.value && accountNo && ![CUSTOMER_ACCOUNT_NO, SUPPLIER_ACCOUNT_NO].includes(accountNo)) {
            const genLeg = await getGeneralLedger({
                accountNo,
                ...ledgerDateRange.value
            }, remoteTenant.value.organizationId)
            if (genLeg && genLeg.ledgerAccounts && genLeg.ledgerAccounts.length === 1) {
                remoteLedger.value = genLeg.ledgerAccounts[0]
            } else {
                remoteLedger.value = undefined
            }
        } else if(!accountNo) {
            remoteLedger.value = undefined
        }
    }

    const fetchLocalSubLedger = async (subAccountUuid?: string) => {
        if (!subAccountUuid) {
            return
        }
        localLedger.value = await fetchSubAccountLedger(localAccount.value === CUSTOMER_ACCOUNT_NO ? CUSTOMER : SUPPLIER,
            subAccountUuid,
            {
                ...dateRange.value
            }
        )
    }

    const fetchRemoteSubLedger = async (subAccountUuid?: string) => {
        if (!subAccountUuid || !remoteTenant.value) {
            return
        }
        remoteLedger.value = await fetchSubAccountLedger(remoteAccount.value === CUSTOMER_ACCOUNT_NO ? CUSTOMER : SUPPLIER,
            subAccountUuid,
            {
                ...dateRange.value
            },
            remoteTenant.value.organizationId
        )
    }

    const clearLocalSelection = () => {
        localAccount.value = undefined
        localSubAccount.value = undefined
    }

    emitter.on(ORG_SELECTED, () => {
        fetchCrossTenantAccounts()
        if (localAccount.value)
            fetchLocalLedger(localAccount.value).then()
        if (localSubAccount.value) {
            clearLocalSelection()
        }
    })

    const fetchCrossTenantAccounts = async () => {
        crossTenantAccounts.value = await get<CrossTenantAccountLink[]>('/cross-tenant/accounts') ?? []
    }

    const saveCrossTenantAccount = async (description = '') => {
        if(!localAccount.value || !remoteTenant.value || !remoteAccount.value) {
            console.error('Invalid cross-tenant account data')
            return
        }
        post('/cross-tenant', {
            localAccountNo: localAccount.value,
            remoteTenantId: remoteTenant.value.organizationId,
            remoteAccountNo: remoteAccount.value,
            description
        }).then(() => {
            fetchCrossTenantAccounts()
        })
    }

    return {
        dateRange,
        localAccount,
        localSubAccount,
        localCustomers,
        localSuppliers,
        computedRemote,
        localLedger,
        isAccountLedger,
        crossTenantAccounts,
        saveCrossTenantAccount,
        fetchCrossTenantAccounts,
        fetchLocalLedger,
        fetchLocalSubLedger,
        localBalance,
        remoteOrgs,
        remoteTenant,
        remoteAccount,
        remoteSubAccount,
        remoteAccounts,
        remoteCustomers,
        remoteSuppliers,
        remoteLedger,
        localSearchParams,
        remoteSearchParams,
        fetchRemoteLedger,
        fetchRemoteSubLedger,
        remoteBalance,
        getRemoteTenantName
    }
}